import { ApiError, request, requestWithAuth } from "./request";
import {
  ReviewsResponse,
  ReviewStats,
  CreateReviewRequest,
  CreateReviewResponse,
  ToggleLikeResponse,
  AddCommentRequest,
  AddCommentResponse,
  RatingFactorsResponse,
  ReviewActivityRow,
  Review,
} from "../types/review";

// `requestWithAuth` unwraps `{ ok, data }`, so this is the `data` half of the assign-user response.
export type AssignReviewUserResult = {
  reviewId: string;
  userId: string;
  pointsAwarded: number;
  pointsReversed: number;
  previousUserId?: string;
};

export type ReviewAuthorHiddenLabel = "female" | "male";

export type ReviewProvenanceResult = {
  reviewId: string;
  sourceRemovedAt: string | null;
  authorHidden: ReviewAuthorHiddenLabel | null;
};

export const reviewService = {
  // Get reviews for a store
  getStoreReviews: async ({
    storeId,
    page = 1,
    limit = 10,
    sortBy = "newest",
    source,
    rating,
    authenticity,
    imageFilter,
  }: {
    storeId: string;
    page?: number;
    limit?: number;
    sortBy?: "newest" | "oldest" | "rating_high" | "rating_low" | "most_liked";
    source?: string;
    rating?: number;
    // Server-side trusted/flagged split for the store-page tabs. Omitted → today's behaviour.
    authenticity?: "trusted" | "flagged" | "all";
    // "with" → only reviews with at least one image. Omitted → no image condition.
    imageFilter?: "with" | "without" | "all";
  }): Promise<ReviewsResponse> => {
    const params: Record<string, unknown> = { page, limit, sortBy };
    if (source) params.source = source;
    if (rating) params.rating = rating;
    if (authenticity) params.authenticity = authenticity;
    if (imageFilter) params.imageFilter = imageFilter;
    return request<ReviewsResponse>({
      path: `/reviews/stores/${storeId}`,
      method: "GET",
      params,
    });
  },

  // Get review stats for a store (standalone, for sidebar)
  // Note: request() unwraps { ok, data: T } → T, so the actual return is ReviewStats directly
  getStoreReviewStats: async ({ storeId }: { storeId: string }): Promise<ReviewStats> => {
    return request<ReviewStats>({
      path: `/reviews/stores/${storeId}/stats`,
      method: "GET",
    });
  },

  // Upload images for a review (base64-encoded, no multer)
  uploadReviewImages: async ({
    reviewId,
    images,
  }: {
    reviewId: string;
    images: File[];
  }): Promise<{ ok: boolean; data?: { images: string[] }; error?: string }> => {
    const toBase64 = (file: File): Promise<string> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

    const encoded = await Promise.all(
      images.map(async (file) => ({
        data: await toBase64(file),
        mimeType: file.type,
        fileName: file.name,
      }))
    );

    // request() unwraps { ok, data: T } → T, so runtime return is { images: string[] }
    return requestWithAuth<{ images: string[] }>({
      path: `/reviews/${reviewId}/images`,
      method: "POST",
      data: { images: encoded },
    });
  },

  // Get user's reviews
  getUserReviews: async ({
    userId,
    page = 1,
    limit = 10,
    sortBy = "newest",
  }: {
    userId: string;
    page?: number;
    limit?: number;
    sortBy?: "newest" | "oldest" | "rating_high" | "rating_low" | "most_liked";
  }): Promise<ReviewsResponse> => {
    return requestWithAuth<ReviewsResponse>({
      path: `/reviews/users/${userId}`,
      method: "GET",
      params: { page, limit, sortBy },
    });
  },

  // Create a review
  createReview: async ({
    storeId,
    reviewData,
  }: {
    storeId: string;
    reviewData: CreateReviewRequest;
  }): Promise<CreateReviewResponse> => {
    return requestWithAuth<CreateReviewResponse>({
      path: `/reviews/stores/${storeId}`,
      method: "POST",
      data: reviewData,
    });
  },

  // Update a review
  updateReview: async ({
    reviewId,
    reviewData,
  }: {
    reviewId: string;
    reviewData: Partial<CreateReviewRequest>;
  }): Promise<CreateReviewResponse> => {
    return requestWithAuth<CreateReviewResponse>({
      path: `/reviews/${reviewId}`,
      method: "PUT",
      data: reviewData,
    });
  },

  // Delete a review
  deleteReview: async (reviewId: string): Promise<{ ok: boolean; error?: string }> => {
    return requestWithAuth<{ ok: boolean; error?: string }>({
      path: `/reviews/${reviewId}`,
      method: "DELETE",
    });
  },

  // Like/unlike a review
  toggleLike: async (reviewId: string): Promise<ToggleLikeResponse> => {
    return requestWithAuth<ToggleLikeResponse>({
      path: `/reviews/${reviewId}/like`,
      method: "POST",
    });
  },

  // Add comment to review
  addComment: async ({
    reviewId,
    commentData,
  }: {
    reviewId: string;
    commentData: AddCommentRequest;
  }): Promise<AddCommentResponse> => {
    return requestWithAuth<AddCommentResponse>({
      path: `/reviews/${reviewId}/comments`,
      method: "POST",
      data: commentData,
    });
  },

  // Reassign a review to a different store (admin only)
  reassignReview: async ({
    reviewId,
    storeId,
  }: {
    reviewId: string;
    storeId: string;
  }): Promise<{ ok: boolean; error?: string }> => {
    return requestWithAuth<{ ok: boolean; error?: string }>({
      path: `/reviews/${reviewId}/reassign`,
      method: "PUT",
      data: { storeId },
    });
  },

  // Move a Facebook group review back to the unmatched sentinel store (admin only)
  unmatchReview: async ({
    reviewId,
  }: {
    reviewId: string;
  }): Promise<{ ok: boolean; error?: string }> => {
    return requestWithAuth<{ ok: boolean; error?: string }>({
      path: `/reviews/${reviewId}/reassign`,
      method: "PUT",
      data: { unmatch: true },
    });
  },

  // Link an imported (external) review to a registered account; pays that account the review's
  // points (admin only).
  assignReviewUser: async ({
    reviewId,
    userId,
  }: {
    reviewId: string;
    userId: string;
  }): Promise<AssignReviewUserResult> => {
    return requestWithAuth<AssignReviewUserResult>({
      path: `/reviews/${reviewId}/assign-user`,
      method: "PUT",
      data: { userId },
    });
  },

  // Undo that link: reverses the points and hands the review back to the import owner (admin only).
  unassignReviewUser: async ({
    reviewId,
  }: {
    reviewId: string;
  }): Promise<AssignReviewUserResult> => {
    return requestWithAuth<AssignReviewUserResult>({
      path: `/reviews/${reviewId}/assign-user`,
      method: "PUT",
      data: { unassign: true },
    });
  },

  // "Original post removed" note and hiding an imported author's name (admin only). Either field
  // may be sent alone; `authorHidden: false` shows the name again.
  setReviewProvenance: async ({
    reviewId,
    sourceRemoved,
    authorHidden,
  }: {
    reviewId: string;
    sourceRemoved?: boolean;
    authorHidden?: ReviewAuthorHiddenLabel | false;
  }): Promise<ReviewProvenanceResult> => {
    return requestWithAuth<ReviewProvenanceResult>({
      path: `/reviews/${reviewId}/provenance`,
      method: "PUT",
      data: { sourceRemoved, authorHidden },
    });
  },

  // Activity trail for one review (admin only).
  getReviewActivity: async ({
    reviewId,
  }: {
    reviewId: string;
  }): Promise<{ reviewId: string; activity: ReviewActivityRow[] }> => {
    return requestWithAuth<{ reviewId: string; activity: ReviewActivityRow[] }>({
      path: `/reviews/${reviewId}/activity`,
      method: "GET",
    });
  },

  // One live review by id (notification deep links pin it on the store page).
  // Hidden, flagged and unknown ids all come back 404; never throws.
  getReviewById: async (
    reviewId: string
  ): Promise<{ ok: true; data: Review } | { ok: false; status?: number; error: string }> => {
    try {
      const data = await request<Review>({
        path: `/reviews/${reviewId}`,
        method: "GET",
      });
      if (!data || typeof data !== "object") return { ok: false, status: 404, error: "not_found" };
      return { ok: true, data };
    } catch (error) {
      const status = error instanceof ApiError ? error.status : undefined;
      const message = error instanceof Error ? error.message : "request_failed";
      return { ok: false, status, error: message };
    }
  },

  // Short link for a review card's «مشاركة» button. The server returns the long /review/<id> URL
  // when Shlink is down, so `url` is always usable on success.
  getShareLink: async (reviewId: string): Promise<string> => {
    const data = await request<{ url: string; degraded: boolean }>({
      path: `/reviews/${reviewId}/share-link`,
      method: "POST",
    });
    return data.url;
  },

  // Get rating factors for category
  getRatingFactors:async (categorySlug: string): Promise<RatingFactorsResponse> => {
    return request<RatingFactorsResponse>({
      path: `/reviews/categories/${categorySlug}/factors`,
      method: "GET",
    });
  },

  /**
   * The factor keys for a category, as a plain array.
   *
   * `request()` returns `response.data.data`, so it has ALREADY unwrapped the `{ ok, data }`
   * envelope — the `RatingFactorsResponse` annotation on `getRatingFactors` above is a lie at
   * runtime, and every caller that wrote the natural `if (res.ok && res.data)` guard against it
   * silently got `undefined && ...` and rendered nothing. That is why `ReviewForm` has never shown
   * the rating-factor stars, which in turn is why 0 of 26 native reviews carry a single factor:
   * the spec read that as users declining to fill them in, but the control was never on screen.
   *
   * The type annotation above is left alone deliberately: it is part of the shared
   * `RatingFactorsResponse` contract and other code may depend on the name. Callers should use
   * THIS function, which is honest about what comes back and tolerates either shape.
   */
  getRatingFactorKeys: async (categorySlug: string): Promise<string[]> => {
    const response = (await reviewService.getRatingFactors(categorySlug)) as unknown;
    if (Array.isArray(response)) return response as string[];
    const data = (response as RatingFactorsResponse | null)?.data;
    return Array.isArray(data) ? data : [];
  },
};
